import { useCallback, useMemo } from 'react'
import { ResumeWorkExperience } from '@rio/types'
import { Base, CardComponentProps } from 'builder/components/Card'
import DateRangePicker from 'builder/components/DateRangePicker'
import { TextField } from 'builder/components/TextField'
import { useI18n } from 'builder/hooks/useI18n'
import { formatDateRange } from 'builder/utils/formatDateRange'
import { CardContentWrapper, Field, Row } from 'builder/components/Card/styles'
import { withAutoFocus } from 'builder/hocs/AutoFocusHoc'
import { JobTitle } from './JobTitle'
import { PlannedResignationDate } from './PlannedResignationDate'
import { WorkExperienceAdditionalInformation } from './WorkExperienceAdditionalInformation'
import { ReasonForResignation } from './ReasonForResignation'

const EmploymentHistoryCard = (props: CardComponentProps<ResumeWorkExperience>) => {
  const { item, autoFocus, locale, sectionId, onChange } = props
  const { i18n } = useI18n()

  const { id: cardId, title, employer, isDateUntilPresent } = item

  const cardTitle = useMemo(() => {
    const joiningString = ' ' + i18n.t('builder.resume_editor.labels.at') + ' '

    return (
      [title, employer].filter(Boolean).join(joiningString) ||
      i18n.t('builder.resume_editor.not_specified')
    )
  }, [i18n, employer, title])

  const cardSubtitle = formatDateRange(item)

  const updateSimpleField = useCallback(
    (event: { target: { name: string; value: string } } | object) => {
      if ('target' in event) {
        onChange(sectionId, cardId, { [event.target.name]: event.target.value }, true)
      }
    },
    [onChange, sectionId, cardId],
  )

  const updateDescription = useCallback(
    (description: string) => onChange(sectionId, cardId, { description }, true),
    [onChange, sectionId, cardId],
  )

  const updateDateRange = useCallback(
    (value: Partial<ResumeWorkExperience>) => onChange(sectionId, cardId, value, true),
    [onChange, sectionId, cardId],
  )

  const updateSelect = useCallback(
    (name: string, value: string) => onChange(sectionId, cardId, { [name]: value }, true),
    [onChange, sectionId, cardId],
  )

  return (
    <Base {...props} title={cardTitle} subtitle={cardSubtitle}>
      <CardContentWrapper>
        <Row>
          <Field>
            <DateRangePicker
              value={item}
              onChange={updateDateRange}
              currentlyLabel={i18n.t('builder.date_range_picker.currently.work')}
            />
          </Field>

          <Field>
            <TextField
              label={i18n.t('builder.resume_editor.work_experiences_card.employer')}
              name="employer"
              onChange={updateSimpleField}
              value={item.employer || ''}
            />
          </Field>
        </Row>

        <Row>
          {isDateUntilPresent ? (
            <PlannedResignationDate {...{ item, updateSimpleField }} />
          ) : (
            <ReasonForResignation {...{ item, updateSimpleField, updateSelect }} />
          )}
          <JobTitle {...{ item, updateSimpleField, autoFocus }} />
        </Row>

        <Row>
          <WorkExperienceAdditionalInformation
            {...{ item, updateDescription, locale, autoFocus }}
          />
        </Row>
      </CardContentWrapper>
    </Base>
  )
}

export const EmploymentCard = withAutoFocus(EmploymentHistoryCard)
